/**
 * Activity Report Email Template
 * Single template with variables - supports all status types
 */

/**
 * Status color configurations
 */
const statusStyles = {
  clear: {
    color: '#4caf50',
    label: 'Clear',
    headerBg: '#e8f5e9',
  },
  warning: {
    color: '#ff9800',
    label: 'Warning',
    headerBg: '#fff3e0',
  },
  emergency: {
    color: '#f44336',
    label: 'Emergency',
    headerBg: '#ffebee',
  },
  summary: {
    color: '#1976d2',
    label: 'Summary',
    headerBg: '#e3f2fd',
  },
};

/**
 * Determines the template type based on date range and patrol stats
 * @param {string} startDate
 * @param {string} endDate
 * @param {Object} patrolStats
 * @returns {string} 'clear' | 'warning' | 'emergency' | 'summary'
 */
export const determineTemplateType = (startDate, endDate, patrolStats) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24));

  // Multi-day reports get summary template
  if (diffDays > 1) {
    return 'summary';
  }

  // Single day - based on highest severity
  if (patrolStats.emergencyCount > 0) {
    return 'emergency';
  }
  if (patrolStats.warningCount > 0) {
    return 'warning';
  }
  return 'clear';
};

/**
 * Generates the Activity Report email HTML
 * @param {Object} params
 * @param {Object} params.branding - Client branding from getClientBranding()
 * @param {string} params.templateType - 'clear' | 'warning' | 'emergency' | 'summary'
 * @param {string} params.locationName - Location/address name
 * @param {string} params.startDate - Formatted start date
 * @param {string} params.endDate - Formatted end date
 * @param {Object} params.stats - Patrol statistics
 * @returns {string} HTML email content
 */
export const generateActivityReportEmail = ({
  branding,
  templateType,
  locationName,
  startDate,
  endDate,
  stats,
  reportUrl,
}) => {
  const style = statusStyles[templateType] || statusStyles.clear;
  const {
    companyName,
    logoUrl,
    primaryColor,
    companyAddress,
    websiteUrl,
    phone,
  } = branding;

  const {
    totalPatrols = 0,
    clearCount = 0,
    warningCount = 0,
    emergencyCount = 0,
    incompleteCount = 0,
  } = stats;

  return `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Activity Report - ${companyName}</title>
  <style>
    body {
      margin: 0;
      padding: 0;
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      background-color: #f5f5f5;
      color: #333;
    }
    .container {
      max-width: 600px;
      margin: 0 auto;
      background-color: #ffffff;
    }
    .header {
      background-color: ${style.headerBg};
      padding: 30px 20px;
      text-align: center;
      border-bottom: 4px solid ${style.color};
    }
    .logo {
      width: 120px;
      height: 120px;
      object-fit: contain;
      display: block;
      margin: 0 auto 15px auto;
    }
    .status-badge {
      display: inline-block;
      background-color: ${style.color};
      color: #ffffff;
      padding: 8px 24px;
      border-radius: 20px;
      font-size: 14px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 1px;
    }
    .content {
      padding: 30px 25px;
    }
    .info-section {
      background-color: #f8f9fa;
      border-radius: 8px;
      padding: 20px;
      margin-bottom: 25px;
    }
    .info-row {
      display: flex;
      justify-content: space-between;
      padding: 8px 0;
      border-bottom: 1px solid #e9ecef;
    }
    .info-row:last-child {
      border-bottom: none;
    }
    .info-label {
      color: #6c757d;
      font-size: 14px;
    }
    .info-value {
      font-weight: 600;
      color: #333;
      font-size: 14px;
    }
    .stats-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 15px;
      margin-bottom: 25px;
    }
    .stat-card {
      background-color: #f8f9fa;
      border-radius: 8px;
      padding: 15px;
      text-align: center;
    }
    .stat-number {
      font-size: 28px;
      font-weight: 700;
      margin-bottom: 5px;
    }
    .stat-label {
      font-size: 12px;
      color: #6c757d;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
    .stat-total .stat-number { color: ${primaryColor}; }
    .stat-clear .stat-number { color: #4caf50; }
    .stat-warning .stat-number { color: #ff9800; }
    .stat-emergency .stat-number { color: #f44336; }
    .summary-list {
      list-style: none;
      padding: 0;
      margin: 0 0 25px 0;
    }
    .summary-list li {
      padding: 10px 0;
      border-bottom: 1px solid #e9ecef;
      font-size: 14px;
    }
    .summary-list li:last-child {
      border-bottom: none;
    }
    .cta-button {
      display: block;
      background-color: ${primaryColor};
      color: #ffffff !important;
      text-decoration: none;
      padding: 14px 28px;
      border-radius: 6px;
      font-weight: 600;
      text-align: center;
      margin: 25px 0;
    }
    .footer {
      background-color: #333;
      color: #ffffff;
      padding: 30px 25px;
      text-align: center;
    }
    .footer-company {
      font-size: 18px;
      font-weight: 600;
      margin-bottom: 10px;
    }
    .footer-address {
      font-size: 13px;
      color: #adb5bd;
      margin-bottom: 15px;
    }
    .footer-link {
      color: ${primaryColor};
      text-decoration: none;
    }
    @media only screen and (max-width: 480px) {
      .stats-grid {
        grid-template-columns: 1fr;
      }
      .content {
        padding: 20px 15px;
      }
    }
  </style>
</head>
<body>
  <div class="container">
    <!-- Header -->
    <div class="header">
      ${logoUrl ? `<img src="${logoUrl}" alt="${companyName}" class="logo" />` : `<h1 style="color: ${primaryColor}; margin: 0 0 15px 0;">${companyName}</h1>`}
      <div class="status-badge">${style.label}</div>
    </div>

    <!-- Content -->
    <div class="content">
      <h2 style="margin: 0 0 20px 0; color: #333;">Activity Report</h2>

      <!-- Info Section -->
      <div class="info-section">
        <div class="info-row">
          <span class="info-label">Location</span>
          <span class="info-value">${locationName}</span>
        </div>
        <div class="info-row">
          <span class="info-label">Period</span>
          <span class="info-value">${startDate} - ${endDate}</span>
        </div>
      </div>

      <!-- Stats Grid -->
      <div class="stats-grid">
        <div class="stat-card stat-total">
          <div class="stat-number" style="color: ${primaryColor};">${totalPatrols}</div>
          <div class="stat-label">Total Patrols</div>
        </div>
        <div class="stat-card stat-clear">
          <div class="stat-number" style="color: #4caf50;">${clearCount}</div>
          <div class="stat-label">Clear</div>
        </div>
        <div class="stat-card stat-warning">
          <div class="stat-number" style="color: #ff9800;">${warningCount}</div>
          <div class="stat-label">Warnings</div>
        </div>
        <div class="stat-card stat-emergency">
          <div class="stat-number" style="color: #f44336;">${emergencyCount}</div>
          <div class="stat-label">Emergencies</div>
        </div>
      </div>

      <!-- Summary List -->
      <ul class="summary-list">
        <li>A total of <strong>${totalPatrols}</strong> patrols were completed during this period.</li>
        <li><strong>${clearCount}</strong> patrols were marked as clear with no issues.</li>
        ${warningCount > 0 ? `<li><strong>${warningCount}</strong> warning(s) were handled by our team.</li>` : ''}
        ${emergencyCount > 0 ? `<li><strong>${emergencyCount}</strong> emergency situation(s) were addressed.</li>` : ''}
        ${incompleteCount > 0 ? `<li><strong>${incompleteCount}</strong> patrol(s) are marked as incomplete.</li>` : ''}
      </ul>

      <!-- CTA Button -->
      <a href="${reportUrl}" class="cta-button">View Full Report</a>
    </div>

    <!-- Footer -->
    <div class="footer">
      <div class="footer-company">${companyName}</div>
      <div class="footer-address">
        ${companyAddress}
        ${phone ? `<br />Phone: ${phone}` : ''}
      </div>
      <a href="${websiteUrl}" class="footer-link">${websiteUrl.replace('https://', '')}</a>
    </div>

    <!-- Powered By -->
    <div style="padding: 12px; text-align: center; background-color: #f5f5f5;">
      <div style="display: inline-block; border: 1px solid #e0e0e0; border-radius: 16px; padding: 6px 16px; background-color: #ffffff;">
        <img src="https://streamdata.com/canappsv3/assets/stream-3771cb3f.png" alt="SDS" style="width: 20px; height: 20px; vertical-align: middle; margin-right: 8px;" />
        <span style="font-size: 13px; color: #666; vertical-align: middle;">Powered by Stream Data Systems</span>
      </div>
    </div>
  </div>
</body>
</html>
  `.trim();
};

export default generateActivityReportEmail;
